import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  TextField,
  Button,
  DialogActions,
  Typography,
  CircularProgress,
} from "@material-ui/core";
import AddBoxOutlinedIcon from "@material-ui/icons/AddBoxOutlined";
import { withFirebase } from "../Firebase";
import { useError } from "../ErrorProvider";
import { getHandoutUploadPayload } from "./handoutUpload";

const AddCompanyDialog = withFirebase(({ firebase, onSuccess }) => {
  const [open, setOpen] = useState(false);
  const [companyCode, setCompanyCode] = useState("");
  const [HREmail, setHREmail] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [winnerCount, setWinnerCount] = useState(1);
  const [quarterlyWinnerCount, setQuarterlyWinnerCount] = useState(1);
  const [handoutFile, setHandoutFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const { showError, showSuccess } = useError();

  const normalizeWinnerCount = (value) => Math.max(1, Number(value) || 1);

  const handleFileChange = (setter) => (event) => {
    const file = event.target.files[0];
    if (file && file.type === "application/pdf") {
      setter(file);
    } else {
      showError("Only PDF files are accepted.");
      event.target.value = null;
    }
  };

  const handleClose = () => {
    setOpen(false);
    setCompanyCode("");
    setHREmail("");
    setCompanyName("");
    setWinnerCount(1);
    setQuarterlyWinnerCount(1);
    setHandoutFile(null);
  };

  const submit = async () => {
    setSubmitting(true);
    try {
      await firebase.createCompany({
        companyCode,
        companyName,
        contactEmails: [HREmail],
        winnerCount: normalizeWinnerCount(winnerCount),
        quarterlyWinnerCount: normalizeWinnerCount(quarterlyWinnerCount),
      });

      if (handoutFile) {
        const uploadPayload = await getHandoutUploadPayload(handoutFile);
        await firebase.uploadCompanyHandout({
          companyCode,
          ...uploadPayload,
        });
      }

      if (typeof onSuccess === "function") onSuccess();
      showSuccess(`Company "${companyName}" created.`);
      handleClose();
    } catch (err) {
      showError(`Error creating company: ${err}`);
    } finally {
      setSubmitting(false);
    }
  };

  const valid = () => companyName && companyCode && HREmail;

  return (
    <>
      <Button
        variant="contained"
        color="primary"
        startIcon={<AddBoxOutlinedIcon />}
        onClick={() => setOpen(true)}
      >
        Add Company
      </Button>
      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        aria-labelledby="form-dialog-title"
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle id="form-dialog-title">Add Company</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Enter the new company's details.
          </DialogContentText>
          <TextField
            autoFocus
            value={companyCode}
            margin="dense"
            id="companyCode"
            label="Company Code"
            type="text"
            variant="outlined"
            onChange={(event) => setCompanyCode(event.target.value)}
            fullWidth
          />
          <TextField
            value={companyName}
            margin="dense"
            id="CompanyName"
            label="Company Name"
            type="text"
            variant="outlined"
            onChange={(event) => setCompanyName(event.target.value)}
            fullWidth
          />
          <TextField
            value={HREmail}
            margin="dense"
            id="HREmail"
            label="HR Email"
            type="email"
            variant="outlined"
            onChange={(event) => setHREmail(event.target.value)}
            fullWidth
          />
          <TextField
            value={winnerCount}
            margin="dense"
            id="winnerCount"
            label="Weekly Winner Count"
            type="number"
            variant="outlined"
            onChange={(event) => setWinnerCount(event.target.value)}
            inputProps={{ min: 1 }}
            fullWidth
          />
          <TextField
            value={quarterlyWinnerCount}
            margin="dense"
            id="quarterlyWinnerCount"
            label="Quarterly Winner Count"
            type="number"
            variant="outlined"
            onChange={(event) => setQuarterlyWinnerCount(event.target.value)}
            inputProps={{ min: 1 }}
            fullWidth
          />
          <div style={{ marginTop: 16 }}>
            <Typography variant="caption" color="textSecondary">
              Handout (PDF)
            </Typography>
            <input
              type="file"
              accept="application/pdf"
              style={{ display: "block", marginTop: 4 }}
              onChange={handleFileChange(setHandoutFile)}
            />
            {handoutFile ? (
              <Typography variant="caption" color="textSecondary">
                Selected: {handoutFile.name}
              </Typography>
            ) : null}
          </div>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} disabled={submitting}>
            Cancel
          </Button>
          <Button
            onClick={submit}
            color="primary"
            variant="contained"
            disabled={!valid() || submitting}
          >
            {submitting ? <CircularProgress size={20} /> : "Add Company"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
});

export default AddCompanyDialog;
